<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Reports</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Occupiers</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <h1 class="mb-0">Occupiers</h1>
            </div>

            <div class="form-body">
              <!-- Table Controls -->
              <div v-if="!loading" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search occupiers..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                      PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                      Excel</a>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading occupiers...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>OG Number</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(occupier, index) in paginatedItems" :key="index" :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td data-label="S/N" class="serial-no">
                          <span>{{ (currentPage - 1) * pageLength + index + 1 }}</span>
                        </td>
                        <td data-label="Name" class="primary-col">{{ occupier.name }}</td>
                        <td data-label="Email" class="secondary-col">{{ occupier.email }}</td>
                        <td data-label="OG Number" class="secondary-col">{{ occupier.og_number }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div v-if="!loading" class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in getPageNumbers" :key="page" class="page-item"
                      :class="{ active: currentPage === page, disabled: page === '...' }">
                      <button class="page-link" @click.prevent="page !== '...' && changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import DashboardLayout from '../layout/DashboardLayout.vue';

import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const mdas = ref([]);
const approvalAuthorities = ref([]);
const occupiers = ref([])
const loading = ref(true)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
  currentPage.value = 1
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

const fetchMDAs = async () => {
  const token = Cookies.get('auth_token');
  try {
    const response = await axios.get(`${root_api_url}/eli/mda`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    mdas.value = response.data.map(mda => ({
      id: mda.id,
      name: mda.name,
    }))

  } catch (error) {
    console.error('Failed to fetch mdas:', error)
  }
}

const fetchApprovalAuthorities = async () => {
  const token = Cookies.get('auth_token');
  try {
    const response = await axios.get(`${root_api_url}/eli/app-auth`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    approvalAuthorities.value = response.data.map(approvalAuthority => ({
      id: approvalAuthority.id,
      name: approvalAuthority.name,
    }))

  } catch (error) {
    console.error('Failed to fetch approval authorities:', error)
  }
}

const fetchOccupiers = async () => {
  const token = Cookies.get('auth_token');
  try {
    const response = await axios.get(`${root_api_url}/eli/occupier`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    occupiers.value = response.data.map(occupier => ({
      id: occupier.id,
      name: occupier.name,
      email: occupier.email,
      og_number: occupier.ogNumber,
    }))
  } catch (error) {
    console.error('Failed to fetch occupiers:', error)
    const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || error?.response?.data?.message || 'Failed to load occupiers. Please try again.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });
  }
}

const filteredItems = computed(() => {
  return occupiers.value.filter(occupier => {
    const term = debouncedSearch.value.toLowerCase()
    return (
      occupier.name.toLowerCase().includes(term) ||
      occupier.email.toLowerCase().includes(term) ||
      occupier.og_number.toLowerCase().includes(term)
    )
  })
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const getPageNumbers = computed(() => {
  const pages = [];
  const delta = 2;
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  pages.push(1); // always show first

  if (current > delta + 2) pages.push('...');

  const start = Math.max(2, current - delta);
  const end = Math.min(total - 1, current + delta);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - delta - 1) pages.push('...');

  pages.push(total); // always show last

  return pages;
});

const exportToPDF = () => {
  const doc = new jsPDF()
  autoTable(doc, {
    head: [['S/N', 'Name', 'Email', 'OG Number']],
    body: occupiers.value.map((occupier, index) => [
      index + 1,
      occupier.name,
      occupier.email,
      occupier.og_number
    ])
  })
  doc.save('occupiers.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('occupiers')
  worksheet.addRow(['S/N', 'Name', 'Email', 'OG Number'])

  occupiers.value.forEach((occupier, index) => {
    worksheet.addRow([
      index + 1,
      occupier.name,
      occupier.email,
      occupier.og_number
    ])
  })

  worksheet.getRow(1).font = { bold: true }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'occupiers.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  loading.value = true
  await fetchMDAs()
  await fetchApprovalAuthorities()
  await fetchOccupiers()
  loading.value = false
  document.title = "View Occupiers"
});

</script>

<style scoped>
/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

/* Component-specific styles */
.primary-col {
  font-weight: 500;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}
</style>
